<template>
  <bm-dropdown
    :items="enumValues"
    :item-text="(o) => $t(o.translationKey)"
    item-value="key"
    v-bind="$attrs"
    :value="keyedValues"
    :searchable="false"
    :sort-fn="sortFn"
    @input="ddlValueChanged"
    @focus="bubbleEvent($event)"
    @blur="bubbleEvent($event)"
    @click:clear="bubbleCustomEvent('click:clear', $event)"
  >
    <!-- Passes all given slots to BmDropdown -->
    <template v-for="(_, slot) of $slots" #[slot]>
      <slot :name="slot" />
    </template>
  </bm-dropdown>
</template>

<script lang="ts">
import BmDropdown from "@/components/controls/input/BmDropdown.vue";
import BmInputField from "@/components/controls/input/mixins/BmInputField";
import type BaseEnumType from "@/app/services/baseEnumType";
import type { PropType } from "vue";
import Vue from "vue";
import type { Comparer } from "@/components/controls/grid/BmGridData";
import userStore from "@/store/modules/userStore";
import { translate } from "@/plugins/i18n";
import Key from "@/app/types/common/key";

export default Vue.extend({
  name: "BmEnumDdl",
  mixins: [BmInputField],
  components: { BmDropdown },
  inheritAttrs: false,
  props: {
    value: {
      type: [Object, Array] as PropType<BaseEnumType[] | BaseEnumType | null>,
      default: null,
    },
    enumType: {
      type: Function as PropType<any>,
      required: true,
    },
    enumFilter: {
      type: Function as PropType<(a: BaseEnumType) => boolean>,
      default: () => () => true,
    },
    /* The value to emit when the control is cleared/empty */
    clearedValue: {
      type: [Object] as PropType<BaseEnumType | null>,
      default: null,
    },
    hideZero: {
      type: Boolean,
      default: false,
    },
    sortedInitialValue: {
      type: Object as PropType<BaseEnumType | undefined>,
      required: false,
    },
  },
  computed: {
    keyedValues(): Number[] | Number | null {
      return this.value === null
        ? null
        : this.value instanceof Array
          ? this.value.map((v) => v.key) // Map values to their keys
          : this.value.key;
    },
    enumValues(): BaseEnumType[] {
      let vals = this.enumType ? (this.enumType.getValues() as any[]) : [];
      vals = vals.filter(this.enumFilter);
      return this.hideZero ? vals.filter((x) => x.key !== 0) : vals;
    },
    sortFn(): Comparer | undefined {
      return this.sortedInitialValue
        ? this.comparerWithDefinedInitialValue(this.sortedInitialValue)
        : undefined;
    },
  },
  methods: {
    ddlValueChanged(val: Array<number> | number | undefined | null) {
      if (val !== undefined && val !== null) {
        if (val instanceof Array) {
          const resolvedValues = val.map((v) => this.enumType.getByKey(v));
          this.$emit("input", resolvedValues);
        } else {
          // Can be undefined when a form is reset
          const resolved = this.enumType.getByKey(val);
          this.$emit("input", resolved);
        }
      } else {
        this.$emit("input", this.clearedValue);
      }
    },
    /**
     * Alphabetically compares enum translations, but ensures
     * that the specified value will remain first in the
     * dropdown
     */
    comparerWithDefinedInitialValue<T extends BaseEnumType>(
      initialValue: T
    ): Comparer {
      return (a: T, b: T) => {
        if (a === initialValue) {
          return -1;
        }

        if (b === initialValue) {
          return 1;
        }

        const translationA = translate(Key.fromString(a.translationKey!));
        const translationB = translate(Key.fromString(b.translationKey!));

        return translationA.localeCompare(
          translationB,
          userStore.state.userLanguageLocale,
          {
            sensitivity: "base",
          }
        ) as 0 | 1 | -1;
      };
    },
  },
});
</script>
